/* eslint-disable react/jsx-no-bind */
import Icon from "~/components/shared/Icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "~/components/shared/DropdownMenu";
import CategoryOptions from "~/components/build/Show/components/Filter/CategoryOptions";
import { removeAllInCategory, removeFilter } from "~/components/build/Show/components/Filter/lib";
import { FilterCategory, SelectedFilter } from "~/components/build/Show/components/Filter/types";

type Props = {
  categories: FilterCategory[];
  selected: SelectedFilter[];
  onChange: (next: SelectedFilter[]) => void;
  /** Test id prefix for individual chips. Defaults to "filter-chip". */
  testId?: string;
};

type SingleChip = {
  kind: "single";
  category: FilterCategory;
  value: string;
  optionLabel: string;
};

type GroupedChip = {
  kind: "grouped";
  category: FilterCategory;
  count: number;
};

type Chip = SingleChip | GroupedChip;

function resolveChips(categories: FilterCategory[], selected: SelectedFilter[]): Chip[] {
  const chips: Chip[] = [];

  for (const category of categories) {
    const selectedForCategory = selected.filter((filter) => filter.categoryKey === category.key);

    // Drop any selections whose option has been removed from the category — they
    // can't be rendered or removed by the user anyway.
    const validSelections = selectedForCategory.filter((filter) =>
      category.options.some((option) => option.value === filter.value),
    );

    if (validSelections.length === 0) {
      continue;
    }

    if (validSelections.length === 1) {
      const filter = validSelections[0];
      const option = category.options.find((opt) => opt.value === filter.value);
      if (!option) {
        continue;
      }
      chips.push({ kind: "single", category, value: filter.value, optionLabel: option.label });
    } else {
      chips.push({ kind: "grouped", category, count: validSelections.length });
    }
  }

  return chips;
}

const chipBaseClassName =
  "shadow-xs flex max-w-full items-stretch overflow-hidden rounded-sm border border-slate-400 bg-white text-sm";
const removeButtonClassName =
  "no-title-tooltip text-charcoal-700 flex w-6 shrink-0 items-center justify-center border-l border-slate-400 hover:bg-gray-100 hover:text-purple-600";
const chipTriggerClassName =
  "flex min-w-0 items-center gap-1 px-1.5 py-0.5 text-left transition-colors hover:bg-gray-100 data-[state=open]:bg-gray-100";

export default function FilterChips({
  categories,
  selected,
  onChange,
  testId = "filter-chip",
}: Props) {
  const chips = resolveChips(categories, selected);

  if (chips.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-wrap items-center gap-1">
      {chips.map((chip) => {
        const { noun, pluralNoun } = chip.category;
        const isGrouped = chip.kind === "grouped";

        // Identity used purely for testIds — varies between single and grouped
        // so consumers can target either layout. NOT used as the React `key`:
        // a stable per-category key keeps the chip's <li> (and the per-chip
        // DropdownMenu inside it) mounted across single ↔ grouped transitions
        // so the menu stays open while toggling extra options.
        const chipTestKey = isGrouped ? chip.category.key : `${chip.category.key}-${chip.value}`;
        const chipTestId = `${testId}-${chipTestKey}`;
        const valueLabel = isGrouped ? (
          <>
            <span className="font-medium tabular-nums">{chip.count}</span> {pluralNoun}
          </>
        ) : (
          <span className="truncate font-medium">{chip.optionLabel}</span>
        );

        const removeAriaLabel = isGrouped
          ? `Remove all ${pluralNoun} filters`
          : `Remove ${noun} is ${chip.optionLabel} filter`;

        const handleRemove = () =>
          onChange(
            isGrouped
              ? removeAllInCategory(selected, chip.category.key)
              : removeFilter(selected, chip.category.key, chip.value),
          );

        return (
          <li key={chip.category.key} data-testid={chipTestId} className={chipBaseClassName}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild={true}>
                <button
                  type="button"
                  data-testid={`${chipTestId}-edit`}
                  className={chipTriggerClassName}
                >
                  <span className="text-charcoal-400 shrink-0 capitalize">{noun}</span>
                  <span className="text-charcoal-400 shrink-0">is</span>
                  <span className="min-w-0 truncate">{valueLabel}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent
                  className="px-0 py-1"
                  align="start"
                  alignOffset={-1}
                  data-testid={`${chipTestId}-menu`}
                >
                  <CategoryOptions
                    category={chip.category}
                    selected={selected}
                    onChange={onChange}
                    testId={chipTestId}
                  />
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
            <button
              type="button"
              aria-label={removeAriaLabel}
              onClick={handleRemove}
              className={removeButtonClassName}
            >
              <Icon icon="heroicons/16/solid/x-mark" className="h-3 w-3" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
